// src/pages/MonthlyProductReport.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import InvoicePopup from "../components/InvoicePopup";
import { getMonthlyProductAbstract, getMonthlyLedger } from "../services/monthlyReportService";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { readStoredFinancialYearStartYear } from "../utils/financialYear";

/**
 * MonthlyProductReport — months open only on Enter key (not on mouse click)
 */

const Tab = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 text-xs border border-gray-400 ${active ? "bg-zinc-800 text-white" : "bg-zinc-200 hover:bg-zinc-300 text-gray-800"}`}
    aria-pressed={active}
  >
    {children}
  </button>
);

function monthsBetween(fromISO, toISO) {
  const out = [];
  const from = new Date(fromISO);
  const to = new Date(toISO);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return out;
  let cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth() + 1;
    const monthKey = `${y}-${String(m).padStart(2, "0")}`;
    const monthLabel = cursor.toLocaleString(undefined, { month: "short", year: "numeric" });
    out.push({ monthKey, monthLabel, date: new Date(cursor) });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return out;
}

export default function MonthlyProductReport() {
  // masters
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");

  // report data
  const [rawRows, setRawRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState("");

  // ledger
  const [ledgerRows, setLedgerRows] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [showLedger, setShowLedger] = useState(false);
  const [popupInvoiceId, setPopupInvoiceId] = useState(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState("");

  // fiscal year default (April → March)
  const fyStartYear = readStoredFinancialYearStartYear();
  const defaultFrom = new Date(fyStartYear, 3, 1);
  const defaultTo = new Date(fyStartYear + 1, 2, 31);
  const [fromDate, setFromDate] = useState(defaultFrom.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(defaultTo.toISOString().slice(0, 10));

  // keyboard navigation for months
  const [highlightIndex, setHighlightIndex] = useState(0);
  const monthListRef = useRef(null);
  const itemInputRef = useRef(null);

  // load item master
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "items"));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, code: d.data().code || "", desc: d.data().desc || "" }));
        setItems(list);
      } catch (err) {
        console.error("Error fetching items master:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // fetch monthly abstract
  useEffect(() => {
    const item = String(selectedItem || "").trim();
    if (!item) {
      setRawRows([]);
      setRowsError("");
      setRowsLoading(false);
      return;
    }
    let mounted = true;
    setRowsLoading(true);
    setRowsError("");
    setRawRows([]);
    setShowLedger(false);
    setSelectedMonthKey("");
    setHighlightIndex(0);

    (async () => {
      try {
        const data = await getMonthlyProductAbstract(item, fromDate, toDate);
        if (!mounted) return;
        setRawRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching monthly product abstract:", err);
        if (mounted) setRowsError("Failed to load monthly summary.");
      } finally {
        if (mounted) setRowsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [selectedItem, fromDate, toDate]);

  // build full month list and map values from rawRows
  const fullMonthRows = useMemo(() => {
    const monthDefs = monthsBetween(fromDate, toDate);
    const map = new Map();
    rawRows.forEach((r) => {
      if (!r) return;
      const key = r.monthKey || r.month || (r.year && r.monthNum ? `${r.year}-${String(r.monthNum).padStart(2, "0")}` : null);
      if (key) map.set(key, r);
    });
    return monthDefs.map((m) => {
      const r = map.get(m.monthKey) || {};
      return {
        monthKey: m.monthKey,
        month: m.monthLabel,
        opening: r.opening ?? 0,
        inward: r.inward ?? 0,
        outward: r.outward ?? 0,
        closing: r.closing ?? 0,
      };
    });
  }, [rawRows, fromDate, toDate]);

  // scroll highlighted into view
  useEffect(() => {
    const node = monthListRef.current;
    if (!node) return;
    const row = node.querySelector(`[data-idx="${highlightIndex}"]`);
    if (row) {
      // ensure visible without huge jumps
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightIndex]);

  // open ledger for monthKey (memoized)
  const openLedgerForMonth = useCallback(
    async (monthKey) => {
      const item = String(selectedItem || "").trim();
      if (!item || !monthKey) return;
      setSelectedMonthKey(monthKey);
      setLoadingLedger(true);
      setLedgerError("");
      setLedgerRows([]);
      try {
        const data = await getMonthlyLedger(item, monthKey);
        setLedgerRows(Array.isArray(data) ? data : []);
        setShowLedger(true);
        setPopupInvoiceId(null);
        setHighlightIndex(0);
      } catch (err) {
        console.error("Error fetching monthly ledger:", err);
        setLedgerError("Failed to load ledger for selected month.");
      } finally {
        setLoadingLedger(false);
      }
    },
    [selectedItem]
  );

  // keyboard handlers (global) - navigate months, open ledger on Enter, focus shortcut '/', Escape closes ledger
  useEffect(() => {
    const handler = (e) => {
      // ignore when focus is on input elements except '/' shortcut
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (e.key === "/" && activeTag !== "input" && activeTag !== "textarea") {
        e.preventDefault();
        itemInputRef.current?.focus?.();
        return;
      }

      if (!selectedItem || showLedger) {
        if (e.key === "Escape" && showLedger) {
          // back from ledger
          setShowLedger(false);
          setLedgerRows([]);
          setSelectedMonthKey("");
          setHighlightIndex(0);
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((hi) => Math.min(hi + 1, fullMonthRows.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((hi) => Math.max(hi - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = fullMonthRows[highlightIndex];
        if (sel) {
          openLedgerForMonth(sel.monthKey);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedItem, showLedger, fullMonthRows, highlightIndex, openLedgerForMonth]);

  // helpers
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    const num = Number(v);
    if (isNaN(num)) return v;
    return num.toLocaleString();
  };
  const safeDate = (d) => {
    if (!d) return "-";
    const dd = new Date(d);
    return isNaN(dd) ? "-" : dd.toLocaleDateString();
  };

  return (
    <div className="h-screen flex flex-col bg-[#f1f2f4]">
      <MenuBar />
      <HeaderBar />

      {/* Tabs */}
      <div className="bg-[#dedfe3] border-x border-b border-gray-400 px-2 py-2 text-xs flex gap-2 overflow-x-auto">
        <Link to="/stock-report" className="px-3 py-1 text-xs border border-gray-400 bg-zinc-200 hover:bg-zinc-300 text-gray-800">
          Stock Status
        </Link>
        <Tab active>Product Analysis</Tab>
        <Link
          to="/movement-analysis"
          className="px-3 py-1 text-xs border border-gray-400 bg-zinc-200 hover:bg-zinc-300 text-gray-800"
        >
          Movement Analysis
        </Link>
      </div>

      {/* Filters */}
      <div className="p-3 border-b flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1">
          <input
            ref={itemInputRef}
            list="item-list"
            className="border rounded px-3 py-2 text-sm w-full sm:w-64"
            placeholder="🔍 Type Item Code and press Enter (or / to focus)"
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setSelectedItem((prev) => String(prev || "").trim());
                setHighlightIndex(0);
              }
            }}
            aria-label="Item code"
          />
          <datalist id="item-list">
            {items.map((it) => (
              <option key={it.id} value={it.code}>
                {it.code} - {it.desc}
              </option>
            ))}
          </datalist>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold">From:</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <label className="text-xs font-semibold">To:</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
            className="text-xs px-2 py-1 border rounded text-red-600"
            aria-label="Clear dates"
          >
            Clear Dates
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 min-h-0 overflow-auto p-3">
        {rowsError && <div className="mb-3 text-xs text-rose-600">{rowsError}</div>}

        {/* Month list */}
        {!showLedger && (
          <div>
            {!selectedItem ? (
              <div className="p-6 text-center text-xs text-gray-500">Select an item to view monthly summary.</div>
            ) : rowsLoading ? (
              <div className="p-6 text-center text-xs text-gray-500">Loading summary…</div>
            ) : (
              <div className="overflow-auto max-h-[70vh] border rounded">
                <table className="w-full max-w-3xl text-xs mx-auto">
                  <thead className="bg-gray-300 sticky top-0 z-10">
                    <tr>
                      <th className="p-2 text-left">Month</th>
                      <th className="p-2 text-right">Opening</th>
                      <th className="p-2 text-right">Inward</th>
                      <th className="p-2 text-right">Outward</th>
                      <th className="p-2 text-right">Closing</th>
                    </tr>
                  </thead>
                  <tbody ref={monthListRef}>
                    {fullMonthRows.map((r, idx) => {
                      const isHighlighted = idx === highlightIndex;
                      return (
                        <tr
                          key={r.monthKey}
                          data-idx={idx}
                          tabIndex={0}
                          onMouseEnter={() => setHighlightIndex(idx)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              openLedgerForMonth(r.monthKey);
                            } else if (e.key === "ArrowDown") {
                              e.preventDefault();
                              setHighlightIndex((hi) => Math.min(hi + 1, fullMonthRows.length - 1));
                            } else if (e.key === "ArrowUp") {
                              e.preventDefault();
                              setHighlightIndex((hi) => Math.max(hi - 1, 0));
                            }
                          }}
                          className={`cursor-default ${isHighlighted ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                          role="row"
                          aria-selected={isHighlighted}
                        >
                          <td className="p-2 border-b">{r.month}</td>
                          <td className="p-2 border-b text-right">{fmt(r.opening)}</td>
                          <td className="p-2 border-b text-right">{fmt(r.inward)}</td>
                          <td className="p-2 border-b text-right">{fmt(r.outward)}</td>
                          <td className="p-2 border-b text-right">{fmt(r.closing)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Ledger */}
        {showLedger && (
          <div>
            <div className="flex items-center gap-3 mb-3">
              <button
                className="px-3 py-1 bg-gray-100 rounded text-xs"
                onClick={() => {
                  setShowLedger(false);
                  setLedgerRows([]);
                  setSelectedMonthKey("");
                  setHighlightIndex(0);
                }}
                aria-label="Back to months"
              >
                ⬅ Back
              </button>
              <h3 className="text-xs font-semibold">
                Ledger — {selectedItem} — {selectedMonthKey}
              </h3>
            </div>

            {loadingLedger ? (
              <div className="p-6 text-center text-xs text-gray-500">Loading ledger…</div>
            ) : ledgerError ? (
              <div className="p-6 text-center text-xs text-rose-600">{ledgerError}</div>
            ) : ledgerRows.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-500">No ledger entries found.</div>
            ) : (
              <div className="overflow-auto max-h-[70vh] border rounded">
                <table className="w-full max-w-3xl text-xs mx-auto">
                  <thead className="bg-gray-300 sticky top-0 z-10">
                    <tr>
                      <th className="p-2">Date</th>
                      <th className="p-2">Voucher No</th>
                      <th className="p-2">Type</th>
                      <th className="p-2">Party</th>
                      <th className="p-2 text-right">Qty IN</th>
                      <th className="p-2 text-right">Qty OUT</th>
                      <th className="p-2 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerRows.map((r, i) => (
                      <tr
                        key={r.id ?? i}
                        className="hover:bg-green-50 cursor-pointer"
                        onClick={() => setPopupInvoiceId(r.id || r.voucherNo)}
                        role="button"
                      >
                        <td className="p-2 border-b">{safeDate(r.date)}</td>
                        <td className="p-2 border-b">{r.voucherNo}</td>
                        <td className="p-2 border-b">{r.type}</td>
                        <td className="p-2 border-b">{r.party}</td>
                        <td className="p-2 border-b text-right">{fmt(r.qtyIn)}</td>
                        <td className="p-2 border-b text-right">{fmt(r.qtyOut)}</td>
                        <td className="p-2 border-b text-right">{fmt(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invoice popup */}
      {popupInvoiceId && <InvoicePopup invoiceId={popupInvoiceId} onClose={() => setPopupInvoiceId(null)} />}
    </div>
  );
}
